import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Lock, Mail, Loader2 } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import {
  markCmsOtpRequested,
  useCmsAuth,
} from "../context/CmsAuthContext";
import { functions } from "../firebase/config";

function mapErr(err) {
  const msg = err?.message || "No se pudo enviar el correo.";
  return msg.replace(/^Firebase:\s*/i, "").replace(/\s*\(.*\)\s*$/, "") || msg;
}

export default function CmsLoginPage() {
  const {
    user,
    isCmsStaff,
    otpVerified,
    mustSetPassword,
    login,
    requestOtp,
    authError,
    loading,
  } = useCmsAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  if (!loading && user && isCmsStaff && otpVerified) {
    if (mustSetPassword) {
      return <Navigate to="/admin/set-password" replace />;
    }
    return <Navigate to="/admin" replace />;
  }
  if (!loading && user && isCmsStaff && !otpVerified) {
    return <Navigate to="/admin/otp" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setSubmitting(true);
    try {
      const loggedUser = await login(email, password);
      try {
        await requestOtp();
        markCmsOtpRequested(loggedUser?.uid);
        try {
          sessionStorage.setItem("cmsOtpAutoSendOk", "1");
          sessionStorage.removeItem("cmsOtpAutoSendErr");
        } catch {
          /* ignore */
        }
      } catch (otpErr) {
        try {
          sessionStorage.setItem(
            "cmsOtpAutoSendErr",
            otpErr?.message || "No se pudo enviar el OTP."
          );
          sessionStorage.removeItem("cmsOtpAutoSendOk");
        } catch {
          /* ignore */
        }
      }
    } catch (err) {
      setError(err?.message || authError || "Credenciales inválidas.");
    } finally {
      setSubmitting(false);
    }
  };

  const onReset = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setSubmitting(true);
    try {
      const fn = httpsCallable(functions, "requestAuthPasswordReset");
      await fn({ email: email.trim(), product: "website" });
      setInfo(
        "Si el correo está registrado, enviamos un enlace desde noreply@agdysa.com. Revise bandeja y spam."
      );
    } catch (err) {
      setError(mapErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050e08] flex items-center justify-center px-6 py-16">
      <form
        onSubmit={mode === "reset" ? onReset : onSubmit}
        className="w-full max-w-md bg-white rounded-[32px] p-8 shadow-2xl space-y-6"
      >
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-brand-primary mb-2">
            AGDYSA CMS
          </p>
          <h1 className="font-display text-3xl font-black text-brand-dark">
            {mode === "reset" ? "Recuperar acceso" : "Mantenimiento"}
          </h1>
          <p className="text-sm text-brand-dark/60 mt-2">
            {mode === "reset"
              ? "Enviaremos un enlace a su correo institucional para definir una nueva contraseña."
              : "Acceso restringido. Inicie sesión con su correo autorizado."}
          </p>
        </div>

        {(error || (mode === "login" && authError)) && (
          <div className="rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm p-4">
            {error || authError}
          </div>
        )}
        {info && (
          <div className="rounded-2xl bg-brand-soft/50 border border-brand-primary/20 text-brand-primary text-sm p-4">
            {info}
          </div>
        )}

        <label className="block space-y-2">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-dark/60">
            Correo electrónico
          </span>
          <div className="relative">
            <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-dark/40" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border border-brand-soft bg-[#f8faf9] pl-11 pr-4 py-3.5 outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder="editor@agdysa.com"
              autoComplete="username"
            />
          </div>
        </label>

        {mode === "login" ? (
          <label className="block space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-dark/60">
              Contraseña
            </span>
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-dark/40" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-2xl border border-brand-soft bg-[#f8faf9] pl-11 pr-4 py-3.5 outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
          </label>
        ) : null}

        <button
          type="submit"
          disabled={submitting || (mode === "reset" && !!info)}
          className="w-full bg-brand-primary text-white py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-brand-dark transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 className="animate-spin" size={16} /> : null}
          {mode === "reset" ? "Enviar enlace" : "Continuar"}
        </button>

        {mode === "login" ? (
          <button
            type="button"
            onClick={() => {
              setMode("reset");
              setError("");
              setInfo("");
            }}
            className="w-full text-center text-xs font-bold text-brand-dark/50 hover:text-brand-primary"
          >
            ¿Olvidó su contraseña?
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              setMode("login");
              setError("");
              setInfo("");
            }}
            className="w-full text-center text-xs font-bold text-brand-dark/50 hover:text-brand-primary"
          >
            Volver al inicio de sesión
          </button>
        )}

        <Link to="/" className="block text-center text-xs text-brand-dark/50 hover:text-brand-primary">
          Volver al sitio público
        </Link>
      </form>
    </div>
  );
}
